import React, { useRef, useState } from 'react';
import { ref as referenciaEnStorage, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { storage } from '../../config/firebase';
import { achicarImagen, extensionDe, revisarImagen, FORMATOS_ACEPTADOS } from '../../lib/imagenes';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';

/**
 * La imagen del negocio: elegirla, cambiarla y sacarla.
 *
 * Es la que se ve arriba de la página pública, al lado del nombre, y la que
 * viaja como imagen de la página cuando alguien comparte el link. Hasta ahora
 * no había forma de cargarla desde ningún lado: el onboarding tenía un
 * `handleLogoChange` escrito pero sin ningún input que lo llamara, así que
 * todos los negocios mostraban las iniciales.
 *
 *
 * POR QUÉ VA A FIREBASE STORAGE Y NO A FIRESTORE
 *
 * El código muerto del onboarding guardaba la imagen como data URL, o sea el
 * archivo entero en base64 adentro del documento del negocio. Un documento de
 * Firestore no puede pasar de 1 MB, y en base64 una foto ocupa un tercio más de
 * lo que pesa: una imagen de 800 KB no entra, y el guardado falla entero —se
 * pierden también el nombre, los horarios y todo lo demás que iba en el mismo
 * documento—. Y aunque entrara, ese documento es el que la página pública lee
 * en cada visita: la foto se descargaría de nuevo con cada turno que alguien
 * saca, sin que el navegador pueda cachearla aparte.
 *
 * Va a `businesses/{uid}/`, que es la carpeta que las reglas de Storage ya le
 * permiten escribir al dueño de la cuenta y a nadie más (storage.rules). En
 * Firestore queda sólo la URL.
 *
 *
 * POR QUÉ LA SUBIDA ES `resumable` Y NO `uploadBytes` A SECAS
 *
 * No por reanudarla: por el porcentaje. `uploadBytes` devuelve una promesa y
 * nada más, así que entre que se elige el archivo y que aparece la imagen no
 * hay NADA para mostrar — y si la conexión está lenta, o si Storage rechaza la
 * escritura, la pantalla se queda igual y quien la mira no sabe si está
 * cargando, si falló o si el botón no anduvo. `uploadBytesResumable` avisa el
 * avance y entrega el error con su código (`storage/unauthorized`,
 * `storage/retry-limit-exceeded`), que es lo que convierte "no pasa nada" en
 * algo que se puede leer y arreglar.
 */

/** Iniciales, lo mismo que muestra la página cuando no hay imagen. */
const iniciales = (nombre) =>
  String(nombre || 'N')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

/**
 * Qué se le dice a la persona según por qué falló.
 *
 * El código de Firebase se muestra igual al final: no significa nada para el
 * negocio, pero es lo primero que hace falta para entender qué pasó cuando
 * llega el reporte de que "no anda".
 */
const explicarFalla = (codigo) => {
  if (codigo === 'storage/unauthorized') {
    return 'El servidor no autorizó la subida. Cerrá sesión, volvé a entrar y probá de nuevo.';
  }
  if (codigo === 'storage/canceled') {
    return 'Se canceló la subida.';
  }
  // Los dos que da un proyecto sin Storage habilitado o con el bucket mal
  // escrito en la configuración: el pedido no llega a ningún lado y el SDK
  // reintenta hasta que se le acaba el tiempo. Le pasó a este proyecto el día
  // que se estrenó esta pantalla, y desde afuera se ve igual que una conexión
  // lenta, así que el mensaje nombra las dos posibilidades.
  if (codigo === 'storage/retry-limit-exceeded' || codigo === 'storage/unknown') {
    return 'No pudimos llegar al servidor de imágenes. Puede ser tu conexión, o que el almacenamiento no esté habilitado en el proyecto.';
  }
  if (codigo === 'storage/quota-exceeded') {
    return 'El almacenamiento del proyecto está lleno.';
  }
  return 'No pudimos subir la imagen. Probá de nuevo en un momento.';
};

/**
 * `prefijo` es con qué empieza el nombre del archivo en Storage. Sirve para
 * distinguir la imagen del negocio de las del personal dentro de la misma
 * carpeta — que tiene que ser la misma, porque las reglas dan permiso sobre
 * `businesses/{uid}/` y sólo sobre un nivel de nombre (storage.rules).
 *
 * `nombre` es de quién es la imagen, para las iniciales de respaldo.
 */
const SelectorDeImagen = ({
  value,
  onChange,
  uid,
  nombreDelNegocio,
  nombre,
  prefijo = 'logo',
}) => {
  const entrada = useRef(null);
  /**
   * `null` cuando no está pasando nada; si no, en qué anda.
   *
   * Son dos etapas y no una: achicar la imagen ocupa la pantalla un momento
   * antes de que empiece a subir, y con un solo estado ese rato se ve como una
   * subida trabada en 0%.
   */
  const [progreso, setProgreso] = useState(null); // { etapa: 'achicando' | 'subiendo', porcentaje }
  const [error, setError] = useState('');

  const subiendo = progreso !== null;

  const elegir = () => {
    setError('');
    entrada.current?.click();
  };

  const alElegirArchivo = async (evento) => {
    const archivo = evento.target.files?.[0];

    // El input se vacía antes de cualquier otra cosa: sin esto, elegir la misma
    // foto dos veces seguidas —después de un error, que es justo cuando pasa—
    // no dispara `change` porque el valor no cambió, y el botón queda muerto.
    evento.target.value = '';

    if (!archivo) return;

    const problema = revisarImagen(archivo);
    if (problema) {
      setError(problema);
      toast.error('Esa imagen no sirve', { description: problema });
      return;
    }

    if (!uid) {
      const aviso = 'Iniciá sesión de nuevo para poder cambiar la imagen.';
      setError(aviso);
      toast.error('No pudimos subir la imagen', { description: aviso });
      return;
    }

    setError('');
    setProgreso({ etapa: 'achicando', porcentaje: 0 });

    let achicada;
    try {
      achicada = await achicarImagen(archivo);
    } catch (fallo) {
      console.error('Error preparando la imagen del negocio:', fallo);
      const aviso = fallo.message || 'No pudimos abrir esa imagen.';
      setProgreso(null);
      setError(aviso);
      toast.error('No pudimos usar esa imagen', { description: aviso });
      return;
    }

    const destino = referenciaEnStorage(
      storage,
      // Nombre nuevo en cada subida en vez de pisar siempre "logo.jpg": con un
      // nombre fijo la URL no cambia, y el navegador y el CDN siguen mostrando
      // la imagen vieja hasta que se les vence el caché. La persona cambia la
      // foto, no ve ninguna diferencia y la vuelve a cambiar.
      `businesses/${uid}/${prefijo}-${Date.now()}.${extensionDe(achicada)}`
    );

    const tarea = uploadBytesResumable(destino, achicada, { contentType: achicada.type });
    setProgreso({ etapa: 'subiendo', porcentaje: 0 });

    tarea.on(
      'state_changed',
      (estado) => {
        const porcentaje = estado.totalBytes
          ? Math.round((estado.bytesTransferred / estado.totalBytes) * 100)
          : 0;
        setProgreso({ etapa: 'subiendo', porcentaje });
      },
      (fallo) => {
        // El código va al log y también al texto: sin él, "no pudimos subir la
        // imagen" es indistinguible entre un permiso mal puesto y una conexión
        // cortada, que se arreglan de maneras opuestas.
        console.error('Error subiendo la imagen del negocio:', fallo.code, fallo);
        const aviso = explicarFalla(fallo.code);
        setProgreso(null);
        setError(`${aviso} (${fallo.code || 'sin código'})`);
        toast.error('No pudimos subir la imagen', { description: aviso });
      },
      async () => {
        try {
          const url = await getDownloadURL(tarea.snapshot.ref);
          onChange(url);
          toast.success('Imagen subida', {
            description: 'Ya se ve en la vista previa. Tocá Aplicar para que la vean tus clientes.',
          });
        } catch (fallo) {
          console.error('Error obteniendo la URL de la imagen:', fallo.code, fallo);
          setError('La imagen se subió pero no pudimos leerla. Probá de nuevo.');
          toast.error('No pudimos leer la imagen subida');
        } finally {
          setProgreso(null);
        }
      }
    );
  };

  return (
    <div className="selector-de-imagen">
      <div className="selector-de-imagen__fila">
        <div className="selector-de-imagen__avatar">
          <Avatar className="h-16 w-16 border border-neutral-200">
            {value && <AvatarImage src={value} alt="" className="object-cover" />}
            <AvatarFallback className="bg-[var(--brand-primary,var(--aturno-orange))] text-white">
              {iniciales(nombre || nombreDelNegocio)}
            </AvatarFallback>
          </Avatar>

          {/* El avance va ENCIMA de la imagen y no sólo en el botón: es donde
              está mirando la persona, porque es lo que espera ver cambiar. */}
          {subiendo && (
            <div className="selector-de-imagen__tapa" aria-hidden="true">
              {progreso.etapa === 'subiendo' ? `${progreso.porcentaje}%` : '…'}
            </div>
          )}
        </div>

        <div className="selector-de-imagen__acciones">
          <Button type="button" variant="outline" size="sm" onClick={elegir} disabled={subiendo}>
            {subiendo ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                {progreso.etapa === 'achicando' ? 'Preparando…' : `Subiendo ${progreso.porcentaje}%`}
              </>
            ) : (
              <>
                <ImagePlus className="mr-2 h-4 w-4" aria-hidden="true" />
                {value ? 'Cambiar imagen' : 'Subir imagen'}
              </>
            )}
          </Button>

          {value && !subiendo && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setError('');
                onChange(null);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Quitar
            </Button>
          )}
        </div>
      </div>

      {/* Lo que pasa, en palabras y para lectores de pantalla: el porcentaje de
          arriba es decorativo. */}
      <p className="selector-de-imagen__estado" role="status" aria-live="polite">
        {subiendo
          ? progreso.etapa === 'achicando'
            ? 'Preparando la imagen…'
            : `Subiendo la imagen: ${progreso.porcentaje}%`
          : ''}
      </p>

      <input
        ref={entrada}
        type="file"
        accept={FORMATOS_ACEPTADOS.join(',')}
        onChange={alElegirArchivo}
        className="hidden"
        tabIndex={-1}
      />

      {/* La ayuda es corta a propósito: va abajo de un campo que convive con
          otros en una grilla, y cada renglón de más estira la fila entera. */}
      {error ? (
        <p className="selector-de-imagen__error" role="alert">
          {error}
        </p>
      ) : (
        <p className="selector-de-imagen__ayuda">
          Se recorta en cuadrado, con fondo blanco. JPG, PNG o WEBP.
        </p>
      )}
    </div>
  );
};

export default SelectorDeImagen;
